import React, { useEffect, useRef, useState } from 'react';
import { HODE_FLATE, hodeTekster, type HodeTekst } from '@/lib/pdf/pdfHeader';
import { MM_PER_PUNKT, SIDE } from '@/lib/sang/oppsett';
import type { Plassert, SangOppsett } from '@/lib/sang/oppsett';
import type { PdfHode } from '@/lib/sang/pdf';

interface SangSiderProps {
  hode: PdfHode;
  oppsett: SangOppsett;
  akkordfarge: string;
}

/** A4 ved 96 dpi. Brukes bare til arket er målt. */
const STANDARD_BREDDE = 794;
/** Fra toppen av linjen til grunnlinjen med `line-height: 1`. Omtrent likt i Courier New og Helvetica. */
const TOPP_TIL_GRUNNLINJE = '0.77em';

const iProsentAvBredde = (mm: number): string => `${(mm / SIDE.bredde) * 100}%`;
const iProsentAvHoyde = (mm: number): string => `${(mm / SIDE.hoyde) * 100}%`;

/** Det alle tekster har felles: grunnlinjen står på y, og størrelsen følger arket. */
function tekstStil(y: number, storrelse: number, pikselPerMm: number): React.CSSProperties {
  return {
    position: 'absolute',
    top: iProsentAvHoyde(y),
    transform: `translateY(-${TOPP_TIL_GRUNNLINJE})`,
    fontSize: `${storrelse * MM_PER_PUNKT * pikselPerMm}px`,
    lineHeight: 1,
    whiteSpace: 'pre',
  };
}

function elementStil(element: Plassert, pikselPerMm: number, akkordfarge: string): React.CSSProperties {
  return {
    ...tekstStil(element.y, element.storrelse, pikselPerMm),
    left: iProsentAvBredde(element.x),
    fontFamily: "'Courier New', monospace",
    fontWeight: element.type === 'tekst' ? 'normal' : 'bold',
    color: element.type === 'akkord' ? akkordfarge : '#000',
  };
}

function hodeTekstStil(tekst: HodeTekst, pikselPerMm: number): React.CSSProperties {
  return {
    ...tekstStil(tekst.y, tekst.storrelse, pikselPerMm),
    [tekst.side === 'venstre' ? 'left' : 'right']: iProsentAvBredde(tekst.marg),
    fontFamily: 'Helvetica, Arial, sans-serif',
    fontWeight: tekst.vekt,
    color: `rgb(${tekst.farge.join(', ')})`,
  };
}

const SangHode: React.FC<{ hode: PdfHode; pikselPerMm: number }> = ({ hode, pikselPerMm }) => (
  <>
    <div
      data-testid="sang-hode"
      style={{
        position: 'absolute',
        left: '0%',
        top: '0%',
        width: '100%',
        height: iProsentAvHoyde(HODE_FLATE.hoyde),
        backgroundColor: `rgb(${HODE_FLATE.farge.join(', ')})`,
      }}
    />
    {hodeTekster({ title: hode.tittel, artist: hode.artist, transposedKey: hode.toneart }).map((tekst) => (
      <span key={`${tekst.side}-${tekst.y}`} style={hodeTekstStil(tekst, pikselPerMm)}>
        {tekst.tekst}
      </span>
    ))}
  </>
);

/** Tegner oppsettet som hvite A4-ark, med samme plassering som PDF-en. */
export const SangSider: React.FC<SangSiderProps> = ({ hode, oppsett, akkordfarge }) => {
  const beholder = useRef<HTMLDivElement>(null);
  const [bredde, setBredde] = useState(STANDARD_BREDDE);

  useEffect(() => {
    const element = beholder.current;
    if (!element) return undefined;
    if (element.clientWidth > 0) setBredde(element.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;

    // En skjult fane måles til 0. Da står siste bredde, så teksten ikke forsvinner.
    const observer = new ResizeObserver(([oppforing]) => {
      if (oppforing.contentRect.width > 0) setBredde(oppforing.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const pikselPerMm = bredde / SIDE.bredde;

  return (
    <div ref={beholder} className="space-y-4" style={{ width: '100%' }}>
      {oppsett.sider.map((side, sideIndeks) => (
        <div
          key={sideIndeks}
          data-testid="sang-ark"
          className="bg-white shadow-md overflow-hidden"
          style={{ position: 'relative', width: '100%', aspectRatio: `${SIDE.bredde} / ${SIDE.hoyde}` }}
        >
          {sideIndeks === 0 && <SangHode hode={hode} pikselPerMm={pikselPerMm} />}
          {side.elementer.map((element, indeks) => (
            <span key={indeks} style={elementStil(element, pikselPerMm, akkordfarge)}>
              {element.tekst}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
};
